/**
 * NoStandingOffersBanner (SILL-512) — a self-contained, self-fetching banner
 * that tells an agent they are invisible to buyers on the marketplace: the
 * SILL-512 invariant is that `active`/`offline_discoverable` availability
 * REQUIRES >=1 standing offer with `status==="active"` (any tier) — targeted
 * M5 `marketplace_offers` are irrelevant here, only the supply-side standing
 * offer catalog counts. An agent can flip their availability dial to "Active"
 * and still have zero live standing offers (the dial and the offer catalog
 * are separately-editable sections), so nothing in the UI otherwise tells
 * them they're unreachable — this banner is that signal.
 *
 * Mounted ONCE by `ProLayout` (see that file) above page content on
 * every `/marketplace/*` route (the Home hero covers Home) — not per-page, so
 * it can't double-fetch or double-fire analytics across the marketplace
 * sub-pages.
 *
 * Visibility (ALL must hold):
 *   - `useMarketplaceEnabled()` is true (the whole marketplace surface is
 *     gated behind this flag; showing the banner when the surface itself is
 *     hidden would point at routes the agent can't reach).
 *   - The setup fetch has succeeded (loading and error states render
 *     nothing — fail QUIET, this is an ambient nudge, not a blocking gate).
 *   - Zero standing offers have `status === "active"` (ANY tier — the
 *     SILL-512 invariant is tier-agnostic).
 *   - Not dismissed this browser session (`sessionStorage`, so it reappears
 *     next session/tab rather than being silenced forever).
 * An active offer always wins over a stale dismiss flag — the dismissed key
 * is only ever consulted once the zero-offers condition is independently
 * true.
 */
import { AlertTriangle, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import { useMarketplaceEnabled } from "../../hooks/useMarketplaceEnabled";
import type { MarketplaceSetupView } from "../../lib/api";
import { IS_PREVIEW } from "../../lib/pro-mode";
import { useMarketplaceSetup } from "../../stores/marketplaceSetup";

const DISMISS_KEY = "sill512.noOffersBanner.dismissed";

// Zero-offer preview fixture — the minimal shape `hasActiveStandingOffer`
// needs. IS_PREVIEW never hits the network (mirrors every other marketplace
// preview page's fixture-instead-of-fetch seam).
const PREVIEW_ZERO_OFFER_SETUP: MarketplaceSetupView = {
  delivery_state: "none",
  blurb: { text: "", max: 280 },
  descriptors: { specialties: [], languages: [], designations: [], licensed_since: null },
  availability: { state: "active" },
  service_area: { max: 15, zips: [] },
  standing_offers: [],
  catalog: {
    items: [],
    cap_matrix: {},
    state_gate_list: [],
    reference_price_cents: 75000000,
    max_primary_zips: 15,
    stale_pending_days: 7,
    derived_earn_up_to_cents: { t1: 0, t2: 0, t3: 0 },
  },
  review_summary: { avg: 0, count: 0, closed_count: 0 },
  reviews: [],
};

function hasActiveStandingOffer(setup: MarketplaceSetupView): boolean {
  return setup.standing_offers.some((offer) => offer.status === "active");
}

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    // Storage unavailable (private mode, disabled cookies, SSR) — default to
    // not-dismissed rather than throw.
    return false;
  }
}

function writeDismissed(): void {
  try {
    sessionStorage.setItem(DISMISS_KEY, "1");
  } catch {
    // Best-effort only — a failed write just means the banner may reappear
    // on the next render, which is harmless.
  }
}

export function NoStandingOffersBanner() {
  const marketplaceEnabled = useMarketplaceEnabled();
  const posthog = usePostHog();

  // The shared setup read (stores/marketplaceSetup.ts) — also feeds the
  // landing gate and the marketplace pages, so the layout-mounted banner adds
  // no request of its own. Loading and error both fail QUIET.
  const read = useMarketplaceSetup({ enabled: marketplaceEnabled });
  const setup = IS_PREVIEW ? PREVIEW_ZERO_OFFER_SETUP : read.data;
  const failed = !IS_PREVIEW && read.error != null;
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed());
  const shownCapturedRef = useRef(false);

  const visible =
    marketplaceEnabled &&
    !failed &&
    setup !== null &&
    !hasActiveStandingOffer(setup) &&
    !dismissed;

  useEffect(() => {
    if (visible && !shownCapturedRef.current) {
      shownCapturedRef.current = true;
      posthog?.capture("marketplace.no_offers_banner_shown");
    }
  }, [visible, posthog]);

  function handleDismiss() {
    writeDismissed();
    setDismissed(true);
    posthog?.capture("marketplace.no_offers_banner_dismissed");
  }

  function handleCtaClick() {
    posthog?.capture("marketplace.no_offers_banner_cta_clicked");
  }

  if (!visible) return null;

  return (
    <div
      role="alert"
      data-testid="no-standing-offers-banner"
      className="flex items-center gap-3 border-b border-warning/30 bg-warning/10 px-4 py-3 text-sm sm:px-8"
    >
      <AlertTriangle
        aria-hidden="true"
        className="h-4 w-4 flex-shrink-0 text-warning"
      />
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-fg">
          You&rsquo;re not reachable on the marketplace
        </p>
        <p className="mt-0.5 text-fg-muted">
          Buyers can&rsquo;t act on your profile until you publish a standing
          offer.{" "}
          <Link
            to="/marketplace/standing-offers"
            onClick={handleCtaClick}
            className="font-semibold text-pro underline"
          >
            Create a standing offer
          </Link>
        </p>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={handleDismiss}
        className="flex-shrink-0 rounded-md p-1 text-fg-muted transition hover:bg-warning/20 hover:text-fg"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
